import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import {validateEmail} from "ui/helpers/validators";
import Form from "ui/elements/form/Form";
import Spinner from "ui/components/spinner/Spinner";
import FatalError from "ui/components/fatal-error/FatalError";
import Ajax from "ui/helpers/Ajax";

/**
 * @typedef {Object} T_SendEmailData
 * @property {string} email
 * @property {string} subject
 * @property {string} message
 */

export default class SendEmail extends Dialog {
	/**
	 * @param {string} email
	 * @param {string} subject
	 * @param {string} message
	 */
	constructor(email, subject, message) {
		super({
			title: i18n('title.send-email'),
			buttons: [
				{
					caption: i18n('buttons.send'),
					role: 'send',
					icon: '/images/icons.svg#icon-svg-envelope'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(result:boolean)}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm(email, subject, message);
	}

	/**
	 * @returns {Promise<boolean>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);

		if (role === 'send') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data).then();
			}
		}
	}

	/**
	 * @private
	 * @param {T_SendEmailData} data
	 * @returns {Promise<void>}
	 */
	async _apply(data) {
		Spinner.show();

		let res;
		try {
			res = await Ajax.post('/common/send-email', {
				email: data.email,
				subject: data.subject,
				message: data.message
			});

			if (res.result === 'error') {
				this._form.showError(res.error, res.fields || []);
				Spinner.hide();
				return;
			}
		} catch (e) {
			FatalError.show(typeof e === 'string' ? e : i18n('errors.http.error'));
			Spinner.hide();
			return;
		}

		Spinner.hide();

		if (this._onComplete) {
			this._onComplete(true);
			this._onComplete = null;
		}

		this.destroy();
	}

	/**
	 * @private
	 * @param {string} email
	 * @param {string} subject
	 * @param {string} message
	 * @returns {Promise<void>}
	 */
	async _buildForm(email, subject, message) {
		const el = document.createElement('div');

		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: {email, subject, message},
			fields: [
				{
					type: 'text',
					name: 'email',
					caption: i18n('inputs.send-email.email'),
					required: true,
					maxlength: 100,
					validators: [validateEmail],
				},
				{
					type: 'text',
					name: 'subject',
					caption: i18n('inputs.send-email.subject'),
					fullWidth: true,
					required: true,
					maxlength: 100
				},
				{
					type: 'textarea',
					name: 'message',
					fullHeight: true,
					required: true,
					caption: i18n('inputs.send-email.message'),
					maxlength: 10000
				}
			]
		});
		this.setContent(el);
	}

	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(false);
			this._onComplete = null;
		}
		super.destroy();
	}
}
